import React, { useEffect } from "react";
import { ButtonComponent } from "@/components";
import { useMutation } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import { ValidationErrors } from "@/types/ValidationError.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { NavLink, useLocation } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { QRCodeCanvas } from "qrcode.react";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { isSNS } from "@/utils/utils";
import bgSNS from "../assets/SVG/bgSNS.svg";
import bgWallet2 from "../assets/SVG/bg_WalletSNS2.svg";

type LocationState = {
  transaction_id: string;
  isComingFromTransactionApproved: boolean;
};

const ReceiptPOS: React.FC = () => {
  const [radioValue, setRadioValue] = React.useState<boolean>(false);

  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const { showSnackBar } = useSnackBarAlert();

  const createReceiptMutation = useMutation<
    ApiResponse<BlobPart>,
    ApiError<ValidationErrors>,
    {
      transaction_id: string;
    }
  >({
    mutationFn: (data) =>
      GenericApi.get<BlobPart>(
        `${ApiRoutes.GENERATE_RECEEIPT}?transaction_id=${
          data?.transaction_id
        }&as_image=${false}`
      ),
    onSuccess: async ({ data }) => {
      if (data) {
        CustomLogger.log("Receipt generated successfully");
        const blob = new Blob([data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const sendEmailMutation = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    unknown
  >({
    mutationFn: () =>
      GenericApi.get<unknown>(
        `${ApiRoutes.SEND_RECEIPT_EMAIL}?transaction_id=${state?.transaction_id}`
      ),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      showSnackBar("Receipt sent to email successfully", "success", {
        vertical: "top",
        horizontal: "center",
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar("Something went wrong", "error", {
        vertical: "top",
        horizontal: "center",
      });
    },
    onSettled: () => {
      window.print();
    },
  });

  useEffect(() => {
    if (state && state.transaction_id) {
      createReceiptMutation.mutate({
        transaction_id: state.transaction_id,
      });
    } else {
      CustomLogger.log("Transaction ID not found.");
    }
  }, []);

  const handlePrint = () => {
    sendEmailMutation.mutate({});
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRadioValue(e.target.checked);
  };

  if (!state || !state.transaction_id) {
    return (
      <div className="flex items-center flex-col justify-between h-screen p-12">
        <h1 className="text-red-500">Transaction ID not found</h1>
      </div>
    );
  }

  console.log(pdfUrl);

  console.log(
    "URL",
    `${GenericApi.baseUrl}${ApiRoutes.GENERATE_RECEEIPT}?transaction_id=${
      state?.transaction_id
    }&as_image=${false}`
  );

  return !isSNS() ? (
    <div className="overflow-hidden pt-[4rem] min-h-screen flex bg-[#F3F3F3] items-center flex-col justify-between p-12">
      <div className="flex justify-center h-full flex-col items-center gap-10 w-full">
        {pdfUrl && !state?.isComingFromTransactionApproved ? (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: "1px solid #ccc" }}
            title="PDF Viewer"
          />
        ) : (
          <QRCodeCanvas
            value={
              `${GenericApi.baseUrl}${
                ApiRoutes.GENERATE_RECEEIPT
              }?transaction_id=${state?.transaction_id}&as_image=${false}` ||
              "https://leonext.exchange/"
            }
            size={250}
            bgColor={"#202a43"}
            fgColor={"#ffffff"}
          /> 
        )}
        <label className="flex items-center justify-between space-x-4 bg-white px-4 py-4 rounded-lg w-full">
          <input
            checked={radioValue}
            onChange={onChange}
            type="checkbox"
            name="receipt"
            className="appearance-none w-4 h-4 rounded-full border border-gray-400 checked:bg-[#1E213D] checked:border-[#1E213D] relative"
          />
          <span className="text-gray-400 text-sm select-none text-right">
            Send receipt on mail
          </span>
        </label>
      </div>

      <ButtonComponent
        title={sendEmailMutation.isPending ? "Sending receipt..." : "Print"}
        actionCallback={handlePrint}
        name={`button--classic w-[10.5rem] mt-5 text-center !bg-[#222b45] !text-white`}
      />

      <NavLink
        replace={true}
        className={"mt-2 underline"}
        to={RoutesPathList.NewClientPOS}
      >
        Clients
      </NavLink>
    </div>
  ) : (
    <div
      className="overflow-hidden pt-[4rem] min-h-screen bg-[#1C1C1C] pb-[6rem] flex flex-col items-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="flex justify-center h-full flex-col items-center gap-10 w-full">
        {pdfUrl && !state?.isComingFromTransactionApproved ? (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: "1px solid #ccc" }}
            title="PDF Viewer"
          />
        ) : (
          <div
            className="relative w-full pb-[6rem] flex justify-center items-center bg-no-repeat bg-contain"
            style={{
              backgroundImage: `url(${bgWallet2})`,
            }}
          >
            <QRCodeCanvas
              value={
                `${GenericApi.baseUrl}${
                  ApiRoutes.GENERATE_RECEEIPT
                }?transaction_id=${state?.transaction_id}&as_image=${false}` ||
                "https://leonext.exchange/"
              }
              size={250}
              bgColor={""}
              fgColor={"#ffffff"}
              className="mt-[5rem]"
            />
          </div>
        )}

        <label className="flex items-center justify-between bg-[#272727] border border-[#8AFEFD] px-4 py-4 rounded-[0.625rem] w-[20.125rem] h-[4.1875rem] cursor-pointer">
          <input
            type="checkbox"
            checked={radioValue}
            onChange={onChange}
            name="receipt"
            className="hidden"
          />

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="33"
            viewBox="0 0 32 33"
            fill="none"
          >
            <circle
              cx="16"
              cy="16.5"
              r="15.5"
              stroke="#8E8E8E87"
              strokeOpacity="0.53"
            />
            {radioValue && <circle cx="16" cy="16.5" r="11" fill="#8AFEFD" />}
          </svg>

          <span className="text-[#919191] text-[1rem] font-[helvetica] select-none text-left ml-4">
            Send receipt on mail
          </span>
        </label>
      </div>

      <ButtonComponent
        title={sendEmailMutation.isPending ? "Sending receipt..." : "Print"}
        actionCallback={handlePrint}
        name={`button_pink mt-[7rem] w-[19.4375rem] mt-5 text-center !text-white`}
      />

      <NavLink
        replace={true}
        className={"mt-3 underline text-[#EE33D9]"}
        to={RoutesPathList.NewClientPOS}
      >
        Clients
      </NavLink>
    </div>
  );
};

export default ReceiptPOS;
